import { useLayoutEffect, useState, type CSSProperties, type RefObject } from 'react';

// Before the first measurement the popover is in the DOM (so it can be measured) but hidden,
// to avoid a flash at (0,0). `useLayoutEffect` computes the real position before paint.
const HIDDEN_STYLE: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  visibility: 'hidden',
  zIndex: 'var(--phx-z-overlay)',
};

function samePlacement(a: CSSProperties, b: CSSProperties): boolean {
  return (
    a.top === b.top &&
    a.bottom === b.bottom &&
    a.left === b.left &&
    a.right === b.right &&
    a.visibility === b.visibility
  );
}

/**
 * Viewport-aware fixed positioning for a popover portalled to `document.body` (so it lies above
 * all PhoenixDesktop windows and is never clipped by a window's `overflow`). Mirrors the
 * async-selector approach: anchored under (or flipped above) the trigger, aligned left (or right),
 * re-measured on scroll / resize / content size change. Returns the inline `style` for the popover.
 */
export function usePopoverPosition(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  popRef: RefObject<HTMLElement | null>
): CSSProperties {
  const [style, setStyle] = useState<CSSProperties>(HIDDEN_STYLE);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(HIDDEN_STYLE);
      return;
    }
    const measure = () => {
      const anchor = anchorRef.current;
      const pop = popRef.current;
      if (!anchor || !pop) return;
      const rect = anchor.getBoundingClientRect();
      const popH = pop.offsetHeight;
      const popW = pop.offsetWidth;
      const margin = 8;

      const downTop = rect.bottom + 4;
      const upBottom = rect.top - 4;
      const spaceBelow = window.innerHeight - downTop;
      const spaceAbove = upBottom;
      const placeUp = spaceBelow < popH + margin && spaceAbove > spaceBelow;
      const alignRight =
        rect.left + popW > window.innerWidth - margin && rect.right - popW > margin;

      const next: CSSProperties = {
        position: 'fixed',
        zIndex: 'var(--phx-z-overlay)',
        ...(placeUp ? { bottom: window.innerHeight - upBottom } : { top: downTop }),
        ...(alignRight ? { right: window.innerWidth - rect.right } : { left: rect.left }),
      };
      setStyle((prev) => (samePlacement(prev, next) ? prev : next));
    };

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    // Re-measure when the popover's own content grows/shrinks (e.g. minute sub-grid expands).
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (observer && popRef.current) observer.observe(popRef.current);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      observer?.disconnect();
    };
  }, [open, anchorRef, popRef]);

  return style;
}
